import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Modal, Spin } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import RichTextEditor from '../../components/RichTextEditor';
import AttachmentUploader from '../../components/knowledgeBase/AttachmentUploader';
import { useAuth } from '../../contexts/AuthContext';
import { getMyReservations, type Reservation } from '../../services/api';
import type { Attachment } from '../../services/knowledgeBase/knowledgeBaseService';
import { literatureService, type Literature } from '../../services/literature/literatureService';
import { resolveUploadAssetUrl } from '../../utils/resolveUploadAssetUrl';
import {
  experimentRecordService,
  ChemistryBlock,
  ExperimentDiscipline,
  ExperimentRecord,
  ExperimentStatus,
  ReagentRow,
  RelatedLiteratureSummary,
  RelatedReservationSummary
} from '../../services/experimentRecords/experimentRecordService';
import {
  coerceReagentRows,
  formatReservationChipSubtitle,
  formatReservationSlot,
  instrumentNameFromReservation,
  normalizeRelatedLitFromRecord,
  normalizeRelatedResFromRecord
} from './experimentRecordShared';
import '../../styles/literature-stitch.css';
import '../../styles/experiment-records-stitch.css';
import '../MemberManagement.css';

const DISCIPLINE_LABEL: Record<ExperimentDiscipline, string> = {
  chemistry: '化学',
  biology: '生物',
  materials: '材料',
  general: '综合'
};

function reservationSummFromApi(r: Reservation): RelatedReservationSummary {
  return {
    _id: String(r._id),
    startTime: r.startTime,
    endTime: r.endTime,
    status: r.status,
    purpose: r.purpose,
    instrumentId: r.instrumentId
  };
}

const ExperimentRecordForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { isDemoBrowseOnly } = useAuth();

  const [loadingDoc, setLoadingDoc] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [discipline, setDiscipline] = useState<ExperimentDiscipline>('chemistry');
  const [status, setStatus] = useState<ExperimentStatus>('draft');
  const [content, setContent] = useState('');
  const [chemistry, setChemistry] = useState<ChemistryBlock>(experimentRecordService.emptyChemistry());
  const [procedureNotes, setProcedureNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [relatedRes, setRelatedRes] = useState<RelatedReservationSummary[]>([]);
  const [relatedLit, setRelatedLit] = useState<RelatedLiteratureSummary[]>([]);

  const [myReservations, setMyReservations] = useState<Reservation[]>([]);
  const [myResLoading, setMyResLoading] = useState(false);
  const [resSearchDraft, setResSearchDraft] = useState('');

  const [litSearchDraft, setLitSearchDraft] = useState('');
  const [litSearchDeb, setLitSearchDeb] = useState('');
  const [litCand, setLitCand] = useState<Literature[]>([]);
  const [litCandLoading, setLitCandLoading] = useState(false);

  const [serverAllowsEdit, setServerAllowsEdit] = useState(true);

  const canEdit = useMemo(() => {
    if (isNew) return !isDemoBrowseOnly;
    return serverAllowsEdit && !isDemoBrowseOnly;
  }, [isNew, serverAllowsEdit, isDemoBrowseOnly]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoadingDoc(true);
    try {
      const { experimentRecord: rec, canEdit } = await experimentRecordService.getById(id);
      setServerAllowsEdit(canEdit);
      setTitle(rec.title);
      setSummary(rec.summary || '');
      setDiscipline(rec.discipline || 'chemistry');
      setStatus(rec.status || 'draft');
      setContent(rec.content || '');
      setChemistry({
        ...experimentRecordService.emptyChemistry(),
        ...(rec.chemistry || {}),
        reagentRows: coerceReagentRows(rec.chemistry)
      });
      setProcedureNotes(rec.procedureNotes || '');
      setTags(rec.tags || []);
      setAttachments(rec.attachments || []);
      setRelatedRes(normalizeRelatedResFromRecord(rec));
      setRelatedLit(normalizeRelatedLitFromRecord(rec));
    } catch (e: unknown) {
      message.error((e as Error).message || '加载失败');
      navigate('/workbench/experiment-records');
    } finally {
      setLoadingDoc(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isNew) {
      setLoadingDoc(false);
      setRelatedRes([]);
      setRelatedLit([]);
      return;
    }
    void load();
  }, [isNew, load]);

  useEffect(() => {
    const t = window.setTimeout(() => setLitSearchDeb(litSearchDraft.trim()), 320);
    return () => window.clearTimeout(t);
  }, [litSearchDraft]);

  const loadMyReservations = useCallback(async () => {
    if (!canEdit) return;
    setMyResLoading(true);
    try {
      const res = await getMyReservations();
      if (res.success && Array.isArray(res.data)) setMyReservations(res.data);
      else setMyReservations([]);
    } catch {
      setMyReservations([]);
    } finally {
      setMyResLoading(false);
    }
  }, [canEdit]);

  useEffect(() => {
    void loadMyReservations();
  }, [loadMyReservations]);

  const resPickRows = useMemo(() => {
    const q = resSearchDraft.trim().toLowerCase();
    return myReservations
      .filter((r) => r._id && !relatedRes.some((x) => x._id === String(r._id)))
      .filter((r) => {
        if (!q) return true;
        const ins =
          r.instrumentId && typeof r.instrumentId === 'object' && 'name' in r.instrumentId
            ? String((r.instrumentId as { name?: string }).name || '').toLowerCase()
            : '';
        const hay = [r.purpose, r.status, ins, r.startTime, r.endTime].join(' ').toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 30);
  }, [myReservations, relatedRes, resSearchDraft]);

  useEffect(() => {
    if (!litSearchDeb) {
      setLitCand([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLitCandLoading(true);
      try {
        const data = (await literatureService.getList({
          keyword: litSearchDeb,
          page: 1,
          limit: 15,
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        })) as { literatures?: Literature[] };
        if (cancelled) return;
        const list = data.literatures || [];
        setLitCand(list.filter((l) => !relatedLit.some((x) => x._id === l._id)));
      } catch {
        if (!cancelled) setLitCand([]);
      } finally {
        if (!cancelled) setLitCandLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [litSearchDeb, relatedLit]);

  const setChem = (key: Exclude<keyof ChemistryBlock, 'reagentRows'>, v: string) => {
    setChemistry((prev) => ({ ...prev, [key]: v }));
  };

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (tags.includes(t)) {
      setTagDraft('');
      return;
    }
    setTags((prev) => [...prev, t]);
    setTagDraft('');
  };

  const removeTag = (t: string) => {
    setTags((prev) => prev.filter((x) => x !== t));
  };

  const buildPayload = () => {
    const rowsFiltered = (chemistry.reagentRows || []).filter(
      (r) => r.compound.trim() || r.molarity.trim() || r.volume.trim()
    );
    return {
      title: title.trim(),
      summary: summary.trim(),
      discipline,
      status,
      content,
      chemistry: { ...chemistry, reagentRows: rowsFiltered },
      procedureNotes,
      tags,
      attachments,
      relatedReservations: relatedRes.map((x) => x._id),
      relatedLiteratures: relatedLit.map((x) => x._id)
    };
  };

  const handleSave = async () => {
    if (!title.trim()) {
      message.warning('请填写标题');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await experimentRecordService.create(buildPayload());
        message.success('已创建');
        navigate(`/workbench/experiment-records/${created._id}/edit`, { replace: true });
      } else if (id) {
        await experimentRecordService.update(id, buildPayload());
        message.success('已保存');
        await load();
      }
    } catch (e: unknown) {
      message.error((e as Error).message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isNew) {
      Modal.confirm({
        title: '放弃新建实验记录？',
        content: '未保存的内容将丢失。',
        okText: '放弃',
        cancelText: '继续编辑',
        okButtonProps: { danger: true },
        onOk: () => navigate('/workbench/experiment-records')
      });
      return;
    }
    void load().then(() => message.info('已恢复为上次保存的内容'));
  };

  const handleFinishLock = async () => {
    if (!title.trim()) {
      message.warning('请填写标题');
      return;
    }
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = { ...buildPayload(), status: 'final' as ExperimentStatus };
      if (isNew) {
        const created = await experimentRecordService.create(payload);
        message.success('已保存并锁定为定稿');
        navigate(`/workbench/experiment-records/${created._id}/edit`, { replace: true });
      } else if (id) {
        await experimentRecordService.update(id, payload);
        setStatus('final');
        message.success('已保存并锁定为定稿');
        await load();
      }
    } catch (e: unknown) {
      message.error((e as Error).message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const reagentRowList = useMemo(
    () => (chemistry.reagentRows?.length ? chemistry.reagentRows : coerceReagentRows(chemistry)),
    [chemistry]
  );

  const addReagentRow = () => {
    setChemistry((prev) => ({
      ...prev,
      reagentRows: [...(prev.reagentRows || coerceReagentRows(prev)), { compound: '', molarity: '', volume: '' }]
    }));
  };

  const updateReagentRow = (i: number, patch: Partial<ReagentRow>) => {
    setChemistry((prev) => {
      const rows = [...(prev.reagentRows || coerceReagentRows(prev))];
      rows[i] = { ...rows[i], ...patch };
      return { ...prev, reagentRows: rows };
    });
  };

  const removeReagentRow = (i: number) => {
    setChemistry((prev) => {
      const rows = [...(prev.reagentRows || coerceReagentRows(prev))];
      rows.splice(i, 1);
      return { ...prev, reagentRows: rows.length ? rows : [{ compound: '', molarity: '', volume: '' }] };
    });
  };

  return (
    <div className="page-container full-height members-lims literature-lims-module er-page">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>工作台</span>
        <span className="mm-crumb-sep">/</span>
        <button type="button" className="mm-crumb-navlink" onClick={() => navigate('/workbench/experiment-records')}>
          实验记录本
        </button>
        <span className="mm-crumb-sep">/</span>
        <span>{isNew ? '新建' : '编辑'}</span>
      </nav>

      {!canEdit && (
        <div className="er-alert er-alert--warn">
          你无权编辑此记录，或当前为演示浏览模式。可返回列表查看有权限的条目。
        </div>
      )}

      <Spin spinning={loadingDoc}>
        <div className="er-curator-main er-curator-main--solo">
          <header className="mm-page-head" style={{ paddingTop: 0 }}>
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 w-100">
              <div>
                <h1 className="mm-title">{isNew ? '新建实验记录' : '编辑实验记录'}</h1>
                <p className="mm-subtitle">按领域加载模板；关联数据可选。保存后团队成员可按权限查看。</p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="er-btn-ghost" onClick={() => navigate('/workbench/experiment-records')}>
                  返回列表
                </button>
                {canEdit ? (
                  <button type="button" className="mm-btn-primary" disabled={saving} onClick={() => void handleSave()}>
                    <LimsNavIcon name="save" sizePx={20} />
                    {saving ? '保存中…' : '保存'}
                  </button>
                ) : null}
              </div>
            </div>
          </header>

          <div className="er-form">
              <section id="er-sec-overview" className="er-card-stitch" style={{ scrollMarginTop: 72 }}>
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    info
                  </span>
                  <h2 className="er-card-stitch-title">基本信息</h2>
                </div>
                <div className="er-field">
                  <label className="er-label" htmlFor="er-title">
                    标题 <span style={{ color: '#ba1a1a' }}>*</span>
                  </label>
                  <input
                    id="er-title"
                    className="er-input-stitch"
                    value={title}
                    disabled={!canEdit}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="填写记录标题…"
                  />
                </div>
                <div className="er-field">
                  <label className="er-label" htmlFor="er-summary">
                    摘要
                  </label>
                  <textarea
                    id="er-summary"
                    className="er-textarea-stitch er-textarea-stitch--sm"
                    value={summary}
                    disabled={!canEdit}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="一句话概括实验目的或结论（列表卡片展示）"
                  />
                </div>
                <div className="row g-3">
                  <div className="col-md-6 er-field">
                    <label className="er-label" htmlFor="er-disc">
                      实验领域
                    </label>
                    <select
                      id="er-disc"
                      className="er-select-stitch"
                      value={discipline}
                      disabled={!canEdit}
                      onChange={(e) => setDiscipline(e.target.value as ExperimentDiscipline)}
                    >
                      {(Object.keys(DISCIPLINE_LABEL) as ExperimentDiscipline[]).map((k) => (
                        <option key={k} value={k}>
                          {DISCIPLINE_LABEL[k]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 er-field">
                    <span className="er-label">状态</span>
                    <div className="er-status-seg">
                      <button
                        type="button"
                        className={status === 'draft' ? 'is-on' : undefined}
                        disabled={!canEdit}
                        onClick={() => setStatus('draft')}
                      >
                        草稿
                      </button>
                      <button
                        type="button"
                        className={status === 'final' ? 'is-on' : undefined}
                        disabled={!canEdit}
                        onClick={() => setStatus('final')}
                      >
                        定稿
                      </button>
                    </div>
                  </div>
                </div>
                <div className="er-field">
                  <span className="er-label">标签</span>
                  <div className="er-tags-cloud">
                    {tags.map((t) => (
                      <span key={t} className="er-tag-chip">
                        {t}
                        {canEdit ? (
                          <button type="button" aria-label={`移除标签 ${t}`} onClick={() => removeTag(t)}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                              close
                            </span>
                          </button>
                        ) : null}
                      </span>
                    ))}
                    {canEdit ? (
                      <input
                        className="er-tag-input"
                        value={tagDraft}
                        onChange={(e) => setTagDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        placeholder="添加标签后回车"
                      />
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="er-card-stitch">
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    link
                  </span>
                  <h2 className="er-card-stitch-title">关联数据</h2>
                </div>
                <p className="er-hint" style={{ marginTop: '-0.5rem' }}>
                  可选。预约须为<strong>本人</strong>预约；文献须为你<strong>有权限</strong>的条目。
                </p>

                <div id="er-sec-instruments" className="er-field" style={{ scrollMarginTop: 72 }}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <span className="er-label mb-0">我的仪器预约</span>
                    {canEdit ? (
                      <span className="er-link-action">
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                          add_circle
                        </span>
                        在下方关联
                      </span>
                    ) : null}
                  </div>
                  {relatedRes.map((r) => (
                    <div key={r._id} className="er-link-row">
                      <div className="er-link-row-icon">
                        <span className="material-symbols-outlined">precision_manufacturing</span>
                      </div>
                      <div className="er-link-row-body">
                        <p className="er-link-row-title">{instrumentNameFromReservation(r)}</p>
                        <p className="er-link-row-sub">{formatReservationSlot(r) || formatReservationChipSubtitle(r)}</p>
                      </div>
                      <Link className="er-rel-link" style={{ fontSize: '0.75rem' }} to="/instruments">
                        查看
                      </Link>
                      {canEdit ? (
                        <button
                          type="button"
                          className="er-link-row-del"
                          aria-label="移除关联预约"
                          onClick={() => setRelatedRes((prev) => prev.filter((x) => x._id !== r._id))}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {canEdit ? (
                    <>
                      <input
                        className="er-input-stitch mt-2"
                        value={resSearchDraft}
                        onChange={(e) => setResSearchDraft(e.target.value)}
                        placeholder="筛选我的预约：仪器、用途、状态…"
                      />
                      {myResLoading ? <p className="er-hint mt-1">加载我的预约中…</p> : null}
                      {!myResLoading && myReservations.length === 0 ? (
                        <p className="er-hint mt-1">暂无本人预约，请先在仪器模块预约。</p>
                      ) : null}
                      {resPickRows.length > 0 ? (
                        <div className="er-pick-panel mt-2" role="listbox" aria-label="候选预约">
                          {resPickRows.map((r) => (
                            <button
                              key={String(r._id)}
                              type="button"
                              className="er-pick-row"
                              onClick={() => {
                                setRelatedRes((prev) => [...prev, reservationSummFromApi(r)]);
                                setResSearchDraft('');
                              }}
                            >
                              <div className="er-pick-row-title">
                                {(r.purpose || '仪器预约').length > 48
                                  ? `${(r.purpose || '仪器预约').slice(0, 48)}…`
                                  : r.purpose || '仪器预约'}
                              </div>
                              <div className="er-pick-row-sub">
                                {formatReservationChipSubtitle(reservationSummFromApi(r))}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>

                <div id="er-sec-literature" className="er-field" style={{ scrollMarginTop: 72 }}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <span className="er-label mb-0">参考文献</span>
                    {canEdit ? (
                      <span className="er-link-action">
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                          search
                        </span>
                        在下方搜索
                      </span>
                    ) : null}
                  </div>
                  {relatedLit.map((r) => (
                    <div key={r._id} className="er-link-row">
                      <div className="er-link-row-icon">
                        <span className="material-symbols-outlined">article</span>
                      </div>
                      <div className="er-link-row-body">
                        <Link className="er-rel-link" to={`/literature/${r._id}`}>
                          {r.title.length > 72 ? `${r.title.slice(0, 72)}…` : r.title}
                        </Link>
                      </div>
                      {canEdit ? (
                        <button
                          type="button"
                          className="er-link-row-del"
                          aria-label={`移除 ${r.title}`}
                          onClick={() => setRelatedLit((prev) => prev.filter((x) => x._id !== r._id))}
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {canEdit ? (
                    <>
                      <input
                        className="er-input-stitch mt-2"
                        value={litSearchDraft}
                        onChange={(e) => setLitSearchDraft(e.target.value)}
                        placeholder="关键词搜索文献库…"
                      />
                      {litCandLoading ? <p className="er-hint mt-1">搜索中…</p> : null}
                      {litCand.length > 0 ? (
                        <div className="er-pick-panel mt-2" role="listbox" aria-label="候选文献">
                          {litCand.map((l) => (
                            <button
                              key={l._id}
                              type="button"
                              className="er-pick-row"
                              onClick={() => {
                                setRelatedLit((prev) => [
                                  ...prev,
                                  {
                                    _id: l._id,
                                    title: l.title,
                                    authors: l.authors,
                                    year: l.year,
                                    journal: l.journal,
                                    doi: l.doi
                                  }
                                ]);
                                setLitSearchDraft('');
                                setLitCand([]);
                              }}
                            >
                              <div className="er-pick-row-title">{l.title}</div>
                              <div className="er-pick-row-sub">
                                {(l.authors || []).slice(0, 2).join(', ')}
                                {l.journal ? ` · ${l.journal}` : ''}
                                {l.year != null ? ` · ${l.year}` : ''}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </section>

              {discipline === 'chemistry' ? (
                <section id="er-sec-chemistry" className="er-card-stitch" style={{ scrollMarginTop: 72 }}>
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      science
                    </span>
                    <h2 className="er-card-stitch-title">化学实验模板</h2>
                  </div>
                  <div className="er-field">
                    <label className="er-label" htmlFor="er-chem-purpose">
                      实验目的
                    </label>
                    <input
                      id="er-chem-purpose"
                      className="er-input-stitch"
                      value={chemistry.objective}
                      disabled={!canEdit}
                      onChange={(e) => setChem('objective', e.target.value)}
                      placeholder="合成或分析的目的简述…"
                    />
                  </div>
                  <div className="er-field">
                    <span className="er-label">试剂与溶剂</span>
                    <div className="er-table-wrap">
                      <table className="er-table-lab">
                        <thead>
                          <tr>
                            <th>化合物</th>
                            <th>浓度 / 摩尔浓度</th>
                            <th>体积</th>
                            <th style={{ width: 56 }} aria-label="操作">
                              {/* */}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {reagentRowList.map((row, i) => (
                            <tr key={i}>
                              <td className="er-compound-cell">
                                <input
                                  value={row.compound}
                                  disabled={!canEdit}
                                  onChange={(e) => updateReagentRow(i, { compound: e.target.value })}
                                  placeholder="化合物名称"
                                />
                              </td>
                              <td>
                                <input
                                  value={row.molarity}
                                  disabled={!canEdit}
                                  onChange={(e) => updateReagentRow(i, { molarity: e.target.value })}
                                  placeholder="—"
                                />
                              </td>
                              <td>
                                <input
                                  value={row.volume}
                                  disabled={!canEdit}
                                  onChange={(e) => updateReagentRow(i, { volume: e.target.value })}
                                  placeholder="—"
                                />
                              </td>
                              <td>
                                {canEdit ? (
                                  <button
                                    type="button"
                                    className="er-link-row-del"
                                    aria-label="删除行"
                                    onClick={() => removeReagentRow(i)}
                                  >
                                    <span className="material-symbols-outlined">more_vert</span>
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {canEdit ? (
                        <button type="button" className="er-table-add-row" onClick={addReagentRow}>
                          + 添加一行
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6 er-field">
                      <label className="er-label" htmlFor="er-chem-cond">
                        条件（温度 / 时间等）
                      </label>
                      <input
                        id="er-chem-cond"
                        className="er-input-stitch"
                        value={chemistry.conditions}
                        disabled={!canEdit}
                        onChange={(e) => setChem('conditions', e.target.value)}
                        placeholder="例如：75°C 反应 4 h"
                      />
                    </div>
                    <div className="col-md-6 er-field">
                      <label className="er-label" htmlFor="er-chem-mon">
                        监测方法
                      </label>
                      <input
                        id="er-chem-mon"
                        className="er-input-stitch"
                        value={chemistry.observations}
                        disabled={!canEdit}
                        onChange={(e) => setChem('observations', e.target.value)}
                        placeholder="TLC, GC-MS…"
                      />
                    </div>
                  </div>
                  <details className="er-chem-advanced">
                    <summary>更多字段（步骤概要、自由文本试剂、后处理）</summary>
                    <div className="er-field mt-2">
                      <label className="er-label" htmlFor="er-chem-po">
                        步骤概要
                      </label>
                      <textarea
                        id="er-chem-po"
                        className="er-textarea-stitch er-textarea-stitch--sm"
                        value={chemistry.procedureOutline}
                        disabled={!canEdit}
                        onChange={(e) => setChem('procedureOutline', e.target.value)}
                      />
                    </div>
                    <div className="er-field">
                      <label className="er-label" htmlFor="er-chem-legacy">
                        试剂与溶剂（自由文本，兼容旧版）
                      </label>
                      <textarea
                        id="er-chem-legacy"
                        className="er-textarea-stitch er-textarea-stitch--sm"
                        value={chemistry.reagentsAndSolvents}
                        disabled={!canEdit}
                        onChange={(e) => setChem('reagentsAndSolvents', e.target.value)}
                      />
                    </div>
                    <div className="er-field">
                      <label className="er-label" htmlFor="er-chem-wu">
                        后处理与分析
                      </label>
                      <textarea
                        id="er-chem-wu"
                        className="er-textarea-stitch er-textarea-stitch--sm"
                        value={chemistry.workupAndAnalysis}
                        disabled={!canEdit}
                        onChange={(e) => setChem('workupAndAnalysis', e.target.value)}
                      />
                    </div>
                  </details>
                </section>
              ) : (
                <section id="er-sec-chemistry" className="er-card-stitch" style={{ scrollMarginTop: 72 }}>
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      biotech
                    </span>
                    <h2 className="er-card-stitch-title">领域备注</h2>
                  </div>
                  <div className="er-field">
                    <label className="er-label" htmlFor="er-proc">
                      步骤与结果
                    </label>
                    <textarea
                      id="er-proc"
                      className="er-textarea-stitch"
                      value={procedureNotes}
                      disabled={!canEdit}
                      onChange={(e) => setProcedureNotes(e.target.value)}
                      placeholder="描述实验流程、关键参数与结果…"
                    />
                  </div>
                </section>
              )}

              <section id="er-sec-narrative" className="er-card-stitch" style={{ scrollMarginTop: 72 }}>
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    edit_note
                  </span>
                  <h2 className="er-card-stitch-title">实验过程叙述</h2>
                </div>
                <p className="er-hint" style={{ marginTop: '-0.5rem' }}>
                  富文本：可插入表格、图片与格式化说明。
                </p>
                {canEdit ? (
                  <div className="er-rich-wrap">
                    <RichTextEditor
                      value={content}
                      onChange={setContent}
                      height={320}
                      placeholder="在此撰写详细的实验过程与记录…"
                    />
                  </div>
                ) : (
                  <div className="er-rich-preview" dangerouslySetInnerHTML={{ __html: content || '<p>（无正文）</p>' }} />
                )}
              </section>

              <section id="er-sec-attachments" className="er-card-stitch er-attachment-zone" style={{ scrollMarginTop: 72 }}>
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    upload_file
                  </span>
                  <h2 className="er-card-stitch-title">附件</h2>
                </div>
                {canEdit ? (
                  <AttachmentUploader
                    variant="curator"
                    value={attachments}
                    onChange={setAttachments}
                    maxCount={12}
                    maxSize={80}
                  />
                ) : attachments.length ? (
                  <ul className="mb-0 ps-3">
                    {attachments.map((a) => (
                      <li key={a.url}>
                        <a href={resolveUploadAssetUrl(a.url) || a.url} target="_blank" rel="noreferrer">
                          {a.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="er-hint mb-0">无附件</p>
                )}
              </section>

              <div className="er-footer-bar">
                {canEdit ? (
                  <>
                    <button type="button" className="er-btn-discard" onClick={handleDiscard}>
                      放弃更改
                    </button>
                    <button
                      type="button"
                      className="er-btn-finish"
                      disabled={saving}
                      onClick={() => void handleFinishLock()}
                    >
                      保存并锁定为定稿
                    </button>
                  </>
                ) : null}
              </div>
            </div>
        </div>
      </Spin>
    </div>
  );
};

export default ExperimentRecordForm;
